import { inject, Inject, Injectable } from '@angular/core';
import { Agent, AgentKeys, Lookup, LookupKeys, ChangeSourceType } from '@ag-common-lib/public-api';
import { FirebaseApp } from 'firebase/app';
import { FIREBASE_APP } from '../../injections/firebase-app';
import { DataService } from '../data.service';
import { agentFromFirestore, getAgentFullName } from './agent-service-utils';
// Two functions share this name. The one above composes the name alone; this one
// appends the primary email, which is what a picker needs to tell two people with the
// same name apart.
import { getAgentFullName as getAgentFullNameWithEmail } from 'ag-common-svc/lib/utils/agent-data.util';
import CustomStore from 'devextreme/data/custom_store';
import { BehaviorSubject, Observable } from 'rxjs';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { QueryParam, WhereFilterOperandKeys } from 'ag-common-svc/lib/dao/CommonFireStoreDao.dao';
import { ChangeSourceTypeService } from 'ag-common-svc/lib/services/change-source-type.service';
import { AgentSearchService } from '../agent-search';
import { compareAsc } from 'date-fns';
import { LoadOptions } from 'devextreme/data';

export const AGENTS_FIRESTORE_COLLECTION = 'agents';

@Injectable({
  providedIn: 'root',
})
export class AgentService extends DataService<Agent> {
  private changeSourceTypeService = inject(ChangeSourceTypeService);
  private agentSearchService = inject(AgentSearchService);

  private _sourceType: ChangeSourceType = this.changeSourceTypeService.sourceType();

  private _showPrimaryEmailSub = new BehaviorSubject<boolean>(false);

  /**
   * DevExtreme lookup DataSource (CustomStore + byKey) backed by `AgentSearchService`.
   * Resolves Created By / agent names via byKey; load is paginated (never isLoadingAll).
   */
  readonly agentsDataSource = this.createAgentsLookupDataSource();
  readonly agentsDataSourceConfiguration = {
    paginate: true,
    pageSize: 25,
    map: data => {
      return Object.assign({}, data, {
        [AgentKeys.p_agent_name]: getAgentFullName(data),
      });
    },
    store: new CustomStore({
      key: BaseModelKeys.dbId,
      byKey: (key: string) => this.agentSearchService.getById(key),
      load: async (loadOptions: LoadOptions) => {
        return this.agentSearchService.search(loadOptions);
      },
    }),
  };

  constructor(@Inject(FIREBASE_APP) fireBaseApp: FirebaseApp) {
    super(fireBaseApp, AgentService.fromFirestore, AgentService.toFirestore);
    super.setCollection(AGENTS_FIRESTORE_COLLECTION);
  }
  static readonly fromFirestore = agentFromFirestore;

  static readonly toFirestore = (data): Agent => {
    return data[AgentKeys.p_email]
      ? Object.assign(data, {
          [AgentKeys.p_email]: data[AgentKeys.p_email].toLowerCase().trim(),
        })
      : data;
  };

  updateAgentFields(
    documentId: string,
    data: Partial<Agent>,
    action: ChangeSourceType = this._sourceType,
  ): Promise<Agent> {
    if (AgentKeys.agent_review_level in data) {
      Object.assign(data, {
        [AgentKeys.registrant_review_level_update_date]: new Date(),
      });
    }

    if (AgentKeys.prospect_status in data) {
      Object.assign(data, {
        [AgentKeys.prospect_status_update_date]: new Date(),
      });
    }
    console.log('UPDATE_AGENT_FIELDS _sourceType ', this._sourceType);
    console.log('%cUPDATE_AGENT_FIELDS sourceType set as ', 'color: green', action);
    Object.assign(data, {
      [BaseModelKeys.actionName]: action,
    });

    return super.updateFields(documentId, data);
  }

  /**
   * The agent holding an address, off the projection rather than a Firestore query.
   *
   * Two behaviour changes, both deliberate. The match now covers the agent's own email
   * addresses as well as `p_email`, because the callers are asking whose address this is
   * and Firestore could only see the primary one. And a collision returns the first
   * match instead of null: this backs a duplicate check, where answering "nobody has
   * this address" because two people do is the worst of the three possible answers.
   */
  getAgentByEmail(email: string): Promise<Agent> {
    return this.agentSearchService.getByEmail(email).then(agents => {
      if (!agents?.length) {
        return null;
      }

      if (agents.length > 1) {
        console.warn(`More than 1 agent found with the email address ${email}`);
      }

      return agents[0];
    });
  }

  getAgentByAgentId(id: string): Promise<Agent> {
    return this.getAllByValue([new QueryParam('p_agent_id', WhereFilterOperandKeys.in, [id, `${id}`])]).then(agents => {
      if (!agents?.length) {
        return null;
      }

      if (agents?.length > 1) {
        console.warn('More than 1 agent found with this agent id');
      }

      agents.sort((left, right) => {
        const leftCreatedDate = left?.[BaseModelKeys?.createdDate];
        const rightCreatedDate = right?.[BaseModelKeys?.createdDate];

        return compareAsc(leftCreatedDate, rightCreatedDate);
      });

      return agents[0];
    });
  }

  getAgentsByAgentIds(ids: string[], sortField: string = 'p_agent_last_name'): Promise<Agent[]> {
    if (!ids?.length) {
      return Promise.resolve([]);
    }
    return this.getAllByValue([new QueryParam(BaseModelKeys.dbId, WhereFilterOperandKeys.in, ids)], null, sortField);
  }

  getMGAsByMGAId(id: string, sortField: string): Observable<Agent[]> {
    const qp = [new QueryParam(AgentKeys.p_mga_id, WhereFilterOperandKeys.equal, id)];

    // `ignoreCash` skips the cached snapshot. The listener is opened with
    // `includeMetadataChanges: true`, so without this it emits twice — once from the
    // local cache with a partial set, then again from the server with the full one.
    // This query returns every agent in an MGA and backs a grid of several thousand
    // rows, so that renders the whole thing twice, the first time with wrong data.
    // One correct render is worth the slightly later first paint.
    return this.getList(qp, { sortField, ignoreCash: true });
  }

  getAgentsByManagerId(id: string, sortField?: string): Promise<Agent[]> {
    const qp: QueryParam[] = [];
    qp.push(new QueryParam(AgentKeys.manager_id, WhereFilterOperandKeys.equal, id));

    return this.getAllByValue(qp, null, sortField);
  }

  getManagersByMGAId(id: string, sortField: string): Promise<Agent[]> {
    let qp: QueryParam[] = [];
    qp.push(new QueryParam('is_manager', WhereFilterOperandKeys.equal, true));
    qp.push(new QueryParam('p_mga_id', WhereFilterOperandKeys.equal, id));

    return this.getAllByValue(qp, null, sortField);
  }

  getAgentCustomStore = () => {
    return new CustomStore({
      key: BaseModelKeys.dbId,
      byKey: (key: string) => this.agentSearchService.getById(key),
      load: async (loadOptions: LoadOptions) => {
        return this.agentSearchService.search(loadOptions);
      },
    });
  };

  getAgentDisplayValue = agentDbId => {
    return this.agentSearchService.getById(agentDbId).then(agent => {
      return getAgentFullName(agent);
    });
  };

  /**
   * Builds a small Lookup list for grid Created By columns from the agent ids present in the rows.
   * Avoids loading the full agents index while still resolving full names.
   */
  getAgentsLookupByIds = async (ids: string[]): Promise<Partial<Lookup>[]> => {
    const uniqueIds = [...new Set((ids ?? []).filter((id): id is string => !!id))];
    if (!uniqueIds.length) {
      return [];
    }

    const agents = await this.agentSearchService.getByIds(uniqueIds);
    return (agents ?? [])
      .map(agent =>
        this.mapAgentToLookup({
          ...agent,
          [BaseModelKeys.dbId]: agent?.[BaseModelKeys.dbId],
        }),
      )
      .filter(Boolean);
  };

  setShowPrimaryEmailInAgentDataSource(show: boolean): void {
    this._showPrimaryEmailSub.next(show);
  }

  /**
   * What a select box asks the server, once its options stop being an array it holds.
   *
   * `searchExpr` is dropped rather than forwarded: a picker searches by whatever it
   * displays, so DevExtreme sends the display field ("description"), which is not a
   * column. Without it the engine searches every searchable agent column instead — name
   * and email — which is what someone typing into an agent picker means.
   */
  private toAgentSearchLoadOptions = (loadOptions: LoadOptions, filter?: unknown): LoadOptions => {
    const { searchExpr, ...rest } = (loadOptions ?? {}) as LoadOptions & { searchExpr?: unknown };
    if (!filter) {
      return rest;
    }

    return { ...rest, filter: rest.filter ? [rest.filter, 'and', filter] : filter };
  };

  /**
   * A `{ value, description }` picker store, paged and searched by the server.
   *
   * Replaces holding the whole directory in memory to populate a dropdown. `byKey` is
   * what makes that possible: a select box with a value it has not loaded a page for can
   * still render its label.
   */
  getAgentsLookupStore = (options?: { filter?: unknown }): CustomStore => {
    return new CustomStore({
      key: LookupKeys.value,
      byKey: async (dbId: string) => {
        const agent = dbId ? await this.agentSearchService.getById(dbId) : null;

        return agent ? { [LookupKeys.value]: dbId, [LookupKeys.description]: getAgentFullNameWithEmail(agent) } : null;
      },
      load: async (loadOptions: LoadOptions) => {
        const response = await this.agentSearchService.search(
          this.toAgentSearchLoadOptions(loadOptions, options?.filter),
        );

        return {
          data: (response?.data ?? []).map(agent => ({
            [LookupKeys.value]: agent?.[BaseModelKeys.dbId],
            [LookupKeys.description]: getAgentFullNameWithEmail(agent),
          })),
          totalCount: response?.totalCount,
        };
      },
    });
  };

  /**
   * Agent rows keyed by Firebase auth uid, which is what an audit trail records as its
   * author — not the document id every other lookup keys on.
   *
   * `uid` is a filterable column but has no point-lookup route, so a single resolution is
   * a one-row search.
   */
  getAgentsStoreByUid = (): CustomStore => {
    return new CustomStore({
      key: AgentKeys.uid,
      byKey: async (uid: string) => {
        if (!uid) {
          return null;
        }

        const response = await this.agentSearchService.search({ filter: [AgentKeys.uid, '=', uid], take: 1 });

        return response?.data?.[0] ?? null;
      },
      load: async (loadOptions: LoadOptions) => {
        const response = await this.agentSearchService.search(this.toAgentSearchLoadOptions(loadOptions));

        return { data: response?.data ?? [], totalCount: response?.totalCount };
      },
    });
  };

  private createAgentsLookupDataSource() {
    return {
      paginate: true,
      pageSize: 25,
      store: new CustomStore({
        key: LookupKeys.value,
        byKey: async (key: string) => {
          if (!key) {
            return null;
          }

          const agent = await this.agentSearchService.getById(key);
          if (!agent) {
            return null;
          }

          // getById may omit dbId when Elastic only has it as document _id
          return this.mapAgentToLookup({
            ...agent,
            [BaseModelKeys.dbId]: agent?.[BaseModelKeys.dbId] ?? key,
          });
        },
        load: async (loadOptions: LoadOptions) => {
          const response = await this.agentSearchService.search({
            ...loadOptions,
            searchExpr: loadOptions?.searchExpr ?? [
              AgentKeys.p_agent_first_name,
              AgentKeys.p_agent_last_name,
              AgentKeys.p_email,
            ],
          });

          const agents = Array.isArray(response?.data) ? response.data : [];
          return {
            data: agents.map(agent => this.mapAgentToLookup(agent)).filter(Boolean),
            totalCount: response?.totalCount ?? agents.length,
          };
        },
      }),
    };
  }

  private mapAgentToLookup(
    agent: Agent,
  ):
    | (Partial<Lookup> & Pick<Agent, AgentKeys.p_agent_first_name | AgentKeys.p_agent_last_name | AgentKeys.p_email>)
    | null {
    if (!agent) {
      return null;
    }

    const value = agent?.[BaseModelKeys.dbId];
    if (!value) {
      return null;
    }

    // description stays name-only for Created By columns; include name/email fields for select-box search/display
    return {
      [LookupKeys.value]: value,
      [LookupKeys.description]: getAgentFullName(agent),
      [AgentKeys.p_agent_first_name]: agent?.[AgentKeys.p_agent_first_name],
      [AgentKeys.p_agent_last_name]: agent?.[AgentKeys.p_agent_last_name],
      [AgentKeys.p_email]: agent?.[AgentKeys.p_email],
    };
  }
}
